/**
 * Auditoría del mapping: cómo se construye el estado financiero.
 *
 * Reúne en una sola página, con pestañas, todo lo que responde de la
 * construcción del documento: de qué cuentas se compone cada renglón, hasta qué
 * apunte de origen baja, en qué difiere del plan de cuentas de Odoo, qué reglas
 * de presentación están vigentes y cómo se reconcilia la estructura técnica
 * contra el estado oficial.
 *
 * Los dos estados de «capa 1» viven aquí como pestañas y no en el menú. Como
 * entradas del menú se titulaban «Estado de Situación Financiera» y «Estado de
 * Resultados», es decir igual que el documento oficial, y una regla BLOQUEANTE
 * —`esf_presentation_matches_statement`— obliga a que sus cifras coincidan: el
 * contador veía dos veces el mismo dinero con dos estructuras distintas sin nada
 * que le dijera cuál era el entregable.
 */

import { useState } from "react";
import { Outlet, useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import type { MappingDivergence } from "../api/types";
import { mayReconcileMapping } from "../api/permissions";
import type { AuditContext } from "./auditContext";
import { useAudit } from "./auditContext";
import { ErrorState, Loading } from "../components/States";
import { StatusBadge } from "../components/StatusBadge";
import { Tabs } from "../components/Tabs";
import type { Tab } from "../components/Tabs";
import { formatDate, formatMoney, moneyClass } from "../components/format";
import { severityTone } from "../components/tones";

/** La página: encabezado, pestañas y la vista elegida. */
export function Audit({ config, session }: AuditContext) {
  const tabs: Tab[] = [
    { to: "/audit", label: "Composición", end: true },
    { to: "/audit/divergences", label: "Divergencias con Odoo" },
    { to: "/audit/rules", label: "Reglas de presentación" },
    { to: "/audit/classification", label: "Clasificación de cuentas" },
  ];
  // Las dos conciliaciones técnicas se ofrecen solo con `mapping:reconcile`.
  // Ocultarlas es comodidad: el endpoint las exige igual.
  if (mayReconcileMapping(session)) {
    tabs.push(
      { to: "/audit/esf", label: "Conciliación técnica · ESF" },
      { to: "/audit/eri", label: "Conciliación técnica · ERI" },
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Auditoría del mapping</h1>
          <p>
            Cada renglón se calcula como una combinación lineal de líneas hoja. Las
            agregaciones suman por jerarquía, no por el texto de la fórmula heredada de
            el sistema de origen: por eso un padre siempre incluye a todos sus hijos.
          </p>
        </div>
        <StatusBadge tone={config.mapping.is_draft ? "serious" : "good"}>
          {config.mapping.version}
        </StatusBadge>
      </div>

      <Tabs tabs={tabs} label="Vistas de auditoría del mapping" />

      <Outlet context={{ config, session } satisfies AuditContext} />
    </>
  );
}

/** Composición de un renglón y sus apuntes de origen. */
export function AuditComposition() {
  const [params, setParams] = useSearchParams();
  const lineId = params.get("line") ?? "";
  const [draft, setDraft] = useState(lineId);

  const composition = useAsync(
    () => api.mappingAudit(lineId || undefined),
    [lineId],
  );
  const detail = useAsync(
    () => (lineId ? api.drilldown(lineId) : Promise.resolve([])),
    [lineId],
  );
  return (
    <>
      <div className="toolbar">
        <div className="field">
          <label htmlFor="line">Renglón</label>
          <input
            id="line"
            type="text"
            placeholder="p. ej. B1207"
            value={draft}
            onChange={(event) => setDraft(event.target.value.toUpperCase())}
          />
        </div>
        <button
          onClick={() => setParams(draft ? { line: draft } : {})}
        >
          Ver composición
        </button>
        {lineId && (
          <button
            onClick={() => {
              setDraft("");
              setParams({});
            }}
          >
            Limpiar
          </button>
        )}
      </div>

      {composition.loading && <Loading what="la composición" />}
      {composition.error && <ErrorState error={composition.error} />}

      {composition.data && (
        <div className="card">
          <h2>Composición {lineId && <span className="mono">{lineId}</span>}</h2>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Renglón</th>
                  <th scope="col">Concepto</th>
                  <th scope="col">Línea hoja</th>
                  <th scope="col">Cuentas</th>
                  <th scope="col" className="num">
                    Coeficiente
                  </th>
                </tr>
              </thead>
              <tbody>
                {composition.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      Sin composición para ese renglón.
                    </td>
                  </tr>
                )}
                {composition.data.slice(0, 400).map((row) => (
                  <tr key={`${row.line_id}-${row.leaf_line_id}`}>
                    <td className="mono">{row.line_id}</td>
                    <td className="small">{row.label}</td>
                    <td className="mono">{row.leaf_line_id}</td>
                    <td className="mono small">
                      {(row.account_prefixes ?? []).join(", ") || "—"}
                    </td>
                    <td className="num">{row.coefficient > 0 ? `+${row.coefficient}` : row.coefficient}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* El drill-down se dibujaba solo cuando traía filas, así que un fallo
          o una carga en curso se veían igual que «este renglón no tiene
          apuntes»: una tabla ausente presentada como un dato completo. */}
      {lineId && detail.loading && <Loading what="los apuntes de origen" />}
      {lineId && detail.error && <ErrorState error={detail.error} />}
      {lineId && detail.data && detail.data.length === 0 && (
        <div className="card">
          <h2>Apuntes de origen</h2>
          <p className="small muted" style={{ marginBottom: 0 }}>
            Este renglón no tiene apuntes en el snapshot publicado.
          </p>
        </div>
      )}

      {lineId && detail.data && detail.data.length > 0 && (
        <div className="card">
          <h2>Apuntes de origen</h2>
          <p className="small muted" style={{ marginTop: 0 }}>
            Trazabilidad hasta <span className="mono">move_line_id</span> en la base de
            el sistema de origen. Estos identificadores permiten reconciliar contra él.
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Fecha</th>
                  <th scope="col">Asiento</th>
                  <th scope="col">Cuenta</th>
                  <th scope="col">Concepto</th>
                  <th scope="col" className="num">
                    Debe
                  </th>
                  <th scope="col" className="num">
                    Haber
                  </th>
                  <th scope="col" className="num">
                    Presentado
                  </th>
                  <th scope="col">ID origen</th>
                </tr>
              </thead>
              <tbody>
                {detail.data.map((row) => (
                  <tr key={row.source_move_line_id}>
                    <td>{formatDate(row.accounting_date)}</td>
                    <td className="mono small">{row.move_name}</td>
                    <td className="mono small">
                      {row.account_code} {row.account_name}
                    </td>
                    <td className="small">{row.line_label ?? "—"}</td>
                    <td className={moneyClass(row.debit)}>{formatMoney(row.debit)}</td>
                    <td className={moneyClass(row.credit)}>{formatMoney(row.credit)}</td>
                    <td className={moneyClass(row.presented_amount)}>{formatMoney(row.presented_amount)}</td>
                    <td className="mono small">{row.source_move_line_id}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

    </>
  );
}

/**
 * Las excepciones declaradas: reglas de presentación, refinamientos y allowlist.
 *
 * Nada de esto se mostraba en la interfaz, y es justo lo que hay que poder
 * enseñar cuando alguien pregunta por qué una cifra no es la suma directa del
 * libro. Una exclusión sin motivo visible es indistinguible de un error.
 */
export function AuditRules() {
  const { config } = useAudit();
  const reglas = config.closing.rules;
  const refinamientos = config.closing.refinements;

  return (
    <>
      <div className="card">
        <h2>Reglas de presentación</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          Una exclusión de ida y vuelta retira del estado un movimiento que entra
          y sale sin efecto económico; una reclasificación mueve un saldo de
          renglón. Las dos alteran la presentación y ninguna cambia el libro: el
          dato original sigue íntegro en el Balance de Comprobación.
        </p>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Regla</th>
                <th scope="col">Estado</th>
                <th scope="col">Tipo</th>
                <th scope="col">Vigencia</th>
                <th scope="col">Motivo y revelación</th>
              </tr>
            </thead>
            <tbody>
              {reglas.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    Ninguna regla de presentación declarada.
                  </td>
                </tr>
              )}
              {reglas.map((regla) => (
                <tr key={regla.id}>
                  <td className="mono small">
                    {regla.id}
                    <div className="small muted">{regla.report}</div>
                  </td>
                  <td>
                    {/* Texto y no distintivo: en el resto de la aplicación los
                        distintivos de aprobación se retiraron por estar siempre
                        encendidos. Aquí el dato SE QUEDA —es la pantalla cuya
                        materia es el estado de las reglas— pero deja de gritar
                        en ámbar desde una tabla entera de reglas en borrador. */}
                    {regla.status === "approved" ? "Aprobada" : "Borrador"}
                    {regla.approved_by && (
                      <div className="small muted">{regla.approved_by}</div>
                    )}
                  </td>
                  <td className="small">
                    {regla.type === "round_trip_exclusion"
                      ? "Exclusión de ida y vuelta"
                      : "Reclasificación"}
                  </td>
                  <td className="small">
                    desde {regla.period_from}
                    {regla.period_to ? ` hasta ${regla.period_to}` : " (sin cierre)"}
                  </td>
                  <td className="small">
                    {regla.reason}
                    {regla.disclosure_text && (
                      <div className="muted">{regla.disclosure_text}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {refinamientos.length > 0 && (
        <div className="card">
          <h2>Refinamientos de renglón</h2>
          <p className="small muted" style={{ marginTop: 0 }}>
            Un prefijo más específico que gana sobre el renglón que refina. No
            añade ni quita dinero: reparte el que ya estaba.
          </p>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Reporte</th>
                  <th scope="col">Renglón</th>
                  <th scope="col">Prefijo</th>
                  <th scope="col">Refina a</th>
                </tr>
              </thead>
              <tbody>
                {refinamientos.map((fila) => (
                  <tr key={`${fila.report}-${fila.line_id}-${fila.prefix}`}>
                    <td className="small">{fila.report}</td>
                    <td className="mono small">{fila.line_id}</td>
                    <td className="mono small">{fila.prefix}</td>
                    <td className="mono small">{fila.refines}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Cuentas excluidas (allowlist)</h2>
        <p className="small muted" style={{ marginTop: 0 }}>
          Una cuenta en la allowlist no cuenta como huérfana. Cada exclusión lleva su
          motivo; ninguna se agrega para silenciar una validación.
        </p>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Prefijo</th>
                <th scope="col">Motivo</th>
              </tr>
            </thead>
            <tbody>
              {config.mapping.allowlist.map((entry) => (
                <tr key={entry.prefix}>
                  <td className="mono">{entry.prefix}</td>
                  <td className="small">{entry.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </>
  );
}

/** Pestaña propia: mapping oficial contra el plan de cuentas de Odoo. */
export function AuditDivergences() {
  const state = useAsync(() => api.mappingDivergences(), []);
  return <Divergences state={state} />;
}

/**
 * Mapping oficial contra `account.group` de Odoo.
 *
 * El mapping es la fuente de PRESENTACIÓN; Odoo, la de VERIFICACIÓN. Esta tabla
 * informa y recomienda; no corrige. Cambiar una etiqueta, por cosmética que
 * parezca, altera el documento que hoy firma el contador, así que la decisión
 * es suya y queda registrada en un PR sobre el YAML.
 */
function Divergences({
  state,
}: {
  state: ReturnType<typeof useAsync<MappingDivergence[]>>;
}) {
  const filas = state.data ?? [];

  return (
    <div className="card">
      <h2>Mapping oficial vs. plan de cuentas de Odoo</h2>
      <p className="small muted">
        Comparación con los <span className="mono">account.group</span> que declara
        Odoo. Ninguna diferencia se aplica automáticamente: la estructura visible
        de los estados la manda el mapping del contador.
      </p>

      {state.loading && <Loading what="las divergencias" />}
      {state.error && <ErrorState error={state.error} />}

      {!state.loading && !state.error && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th scope="col">Tipo</th>
                <th scope="col">Mapping</th>
                <th scope="col">Odoo</th>
                <th scope="col">Impacto</th>
                <th scope="col">Estado</th>
              </tr>
            </thead>
            <tbody>
              {filas.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    Sin divergencias entre el mapping y el plan de cuentas de Odoo.
                  </td>
                </tr>
              )}
              {filas.map((fila, indice) => (
                <tr key={`${fila.divergence_type}-${fila.mapping_ref ?? fila.odoo_ref}-${indice}`}>
                  <td>{fila.divergence_type}</td>
                  <td>
                    <span className="mono">{fila.mapping_ref ?? "—"}</span>
                    {fila.mapping_value && (
                      <div className="small muted">{fila.mapping_value}</div>
                    )}
                  </td>
                  <td>
                    <span className="mono">{fila.odoo_ref ?? "—"}</span>
                    {fila.odoo_value && (
                      <div className="small muted">{fila.odoo_value}</div>
                    )}
                  </td>
                  <td className="small">{fila.impact}</td>
                  <td>
                    <StatusBadge tone={severityTone(fila.severity)}>
                      {fila.severity === "blocking"
                        ? "Bloqueante"
                        : "Pendiente contador"}
                    </StatusBadge>
                    <div className="small muted">{fila.recommendation}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
